import { useEffect, useMemo, useState } from 'react';

import type { OrgStructure } from '@commenda/api';

import type { StructureChartAdapter } from '../adapters';
import { StructureCanvasHelper } from '../canvas';
import {
  StructureLayoutHelper,
  TextMeasureHelper,
  type TextMeasurer,
} from '../layout';
import { OwnershipModelHelper } from '../model';

// Lays the chart out once the page fonts have loaded, so labels and chips are sized in the font they render in.
function useStructureLayout(
  structure: Readonly<OrgStructure>,
  adapter: Readonly<StructureChartAdapter>
) {
  const [measurer, setMeasurer] = useState<TextMeasurer | null>(null);

  useEffect(() => {
    let isCancelled = false;
    document.fonts.ready.then(() => {
      if (!isCancelled) setMeasurer(TextMeasureHelper.createCanvasMeasurer());
    });
    return () => {
      isCancelled = true;
    };
  }, []);

  const model = useMemo(
    () => OwnershipModelHelper.build(structure),
    [structure]
  );
  const frame = useMemo(
    () => adapter.getFrame(structure),
    [adapter, structure]
  );
  const layout = useMemo(
    () =>
      measurer ? StructureLayoutHelper.compute(model, measurer, frame) : null,
    [model, measurer, frame]
  );
  const flowNodes = useMemo(
    () =>
      layout && measurer
        ? StructureCanvasHelper.toFlowNodes(layout, model, measurer)
        : [],
    [layout, model, measurer]
  );

  return { model, frame, layout, flowNodes };
}

export { useStructureLayout };
